import React from "react";
import { cn } from "@/lib/utils";
import { Localizer } from "@/main/react/Types";

/**
 * The seam between a search vertical's real matches and the blended "more you might like" discovery
 * rows that fill out a sparse page (see the blended broaden-and-fill build, design
 * `2026-07-16-search-recall-repair-and-never-empty-results-design.md` §16).
 *
 * It has TWO renderings, because the seam makes two different claims, and shipping only the first one
 * was the defect (`DISCOVERY-3`, `EMPTY-STATES-4`, `EMPTY-STATES-11`; the second label was specified in
 * `2026-07-20-search-strong-match-divider-and-honest-tab-counts.md` on the day the first shipped and
 * then never written):
 *
 * - `strongMatchCount > 0` — a genuine separator. Intentionally light (a hairline plus a muted label,
 *   not a hard stop) so the filler below stays inviting to click while the page stays honest about
 *   where the exact matches end.
 * - `strongMatchCount === 0` — **nothing above it to separate from**, so it is not a separator at all.
 *   It is the page's answer: the query matched nothing and everything below is a suggestion. Since
 *   `EMPTY-STATES-104` it is also styled as an answer rather than as a card — see the comment on the
 *   element itself, which is the whole of that row's fix. That has
 *   to be ANNOUNCED, not merely present. `role="separator"` names an element; it does not tell a
 *   screen-reader user that the answer to their query changed, and an `aria-label` on a separator was
 *   the only cue this state had.
 *
 * ⚠️ **The zero branch carries NO `role="status"` of its own, and that is the fix rather than a
 * regression (`DISCOVERY-38`, 2026-08-23).** It used to. But the element only existed when
 * `strongMatchCount === 0`, and it was mounted inside the mapped result list — so the node and its
 * text entered the DOM together, after the fetch resolved, which is the construction
 * `SearchResultsPage.tsx:917-925` states in its own comment is not reliably announced. The live
 * region now lives one level up, ALWAYS MOUNTED, in `SearchPaginatedList`; this box is its
 * *contents*. Re-adding `role="status"` here would nest a live region inside a live region and
 * double-announce. Verification is honest about its limit: jsdom has no announcement model and
 * passes either construction, so the guards assert the MECHANISM (the region is in the tree before
 * the counts resolve) and the announcement itself is unproven short of a real AT run.
 *
 * The two states carry DIFFERENT `data-playwright-id`s on purpose. A spec asserting "a divider exists"
 * passes in both, which is exactly how the missing label survived from 2026-07-20 to 2026-08-16.
 *
 * `strongMatchCount` omitted keeps the historical separator rendering, so a caller that has not been
 * taught the difference cannot accidentally claim a zero.
 */
export function SearchBlendDivider(props: {
    localizer: Localizer;
    strongMatchCount?: number | null;
    /**
     * True only when EVERY vertical resolved to zero (`DISCOVERY-51`).
     *
     * The zero branch below fires when the ACTIVE vertical is zero, which is a different condition:
     * on `?searchQuery=ethics` the tab strip read Posts 0 · Thoughts 1 · Groups 0 · Series 0 · Users 0
     * and this box still printed "Nothing on Seavien matches that yet", contradicting the badge one row
     * above it. The page has always computed this flag and forwarded it only to `SearchEmptyState`,
     * which the never-empty blend keeps unreachable on Posts / Groups / Series.
     *
     * Absent (`undefined`) means "we do not know", and the honest default there is the sentence that
     * points at the other tabs rather than the one that denies them — the same direction
     * `SearchEmptyState` already takes. No key is minted either way; both strings ship in all 25
     * catalogs.
     */
    allVerticalsEmpty?: boolean;
    /** Spacing supplied by the mount point, because the zero branch is hoisted out of the list flow. */
    className?: string;
}) {
    const { localizer, strongMatchCount, allVerticalsEmpty, className } = props;

    if (strongMatchCount === 0) {
        return (
            <div
                /*
                 * `EMPTY-STATES-104` / `DISCOVERY-46` — NOT a card, and that is the whole fix.
                 *
                 * This box used to be `rounded-xl border border-border bg-card` with centred muted
                 * text: sampled at native resolution its fill was BYTE-IDENTICAL to the suggestion
                 * cards it sits above, in both themes (`#F4F6F7` light, `#191B1E` dark), on the same
                 * border token, differing only by a 4 px corner radius nobody can see at either
                 * viewport. So the single most important thing on the screen — the statement that the
                 * search failed — was painted in the exact recipe of a search result, and a scanner
                 * read it as row zero of the list and took the six boxes below it as the answer.
                 *
                 * Contrast was never the problem and is unchanged (measured 7.05:1 light, 6.81:1 dark).
                 * HIERARCHY was. Rank now comes from typography and alignment, which the filler cannot
                 * borrow without ceasing to be filler: the verdict is foreground-coloured and
                 * semibold at body size, the recovery sentence stays muted beneath it, and the block
                 * is left-aligned because it is a statement ABOUT the page rather than an item in the
                 * list. The cards themselves are untouched — the never-empty blend is a defended
                 * feature and the filler must stay inviting to click, so the answer gains weight
                 * rather than the suggestions losing it.
                 */
                /*
                 * `DISCOVERY-75` — no `px-1`. At 412 px this block's two lines sat 4 CSS px right of
                 * every other left edge in the single column (the query chip, the filter pills, the
                 * primary button and all six card borders all start at 32 px; these started at 37).
                 * A 4 px indent reads as SUBORDINATION, which is the opposite of the typographic rank
                 * `EMPTY-STATES-104` deliberately gave this sentence. The vertical breathing room is
                 * kept (`py-1`); the `> 0` separator branch below never carried `px-1`, so the two
                 * renderings now share one left edge as well as one column.
                 */
                className={cn("space-y-1 py-1 text-left", className)}
                data-playwright-id="search-zero-match-notice"
            >
                {/* `DISCOVERY-43` — two sentences: what happened, then what to do about it. The first
                    describes the product's behaviour and leaves the person whose query just failed
                    with no next move; the second is the recovery line the catalogs have held all
                    along, unreachable here until 2026-08-23 because it lived in `SearchEmptyState`,
                    which `SearchPaginatedList` renders only when `visibleItems.length === 0` — which
                    the never-empty blend prevents. So the same failed query got a recovery sentence
                    on the two verticals that do not blend and none on the three that do. No new key:
                    both sentences ship in all 25 catalogs.

                    ⚠️ CORRECTING `DISCOVERY-43`'s CLOSURE PREMISE, which asserted that "the
                    all-verticals-zero condition that sentence was written for is the condition this
                    notice renders under". It is not: this branch fires on the ACTIVE vertical's zero,
                    and a query with one Thoughts match rendered it on Posts while the tab strip one
                    row above reported the match (`DISCOVERY-51`). Which of the two sentences is true
                    is decided by the `allVerticalsEmpty` prop, not by this component.

                    It matters most on a phone, where there is no visible search field on this route
                    at 412px (`DISCOVERY-30`, `DISCOVERY-56`), so nothing else on screen points at the
                    one control that would let the visitor retry. */}
                {/* `DISCOVERY-66` — a real `h2`, so the verdict is IN the heading outline.
                    A screen-reader user navigating by heading went from the page's `sr-only` h1
                    straight into six result `h2`s and never met the sentence saying their search
                    matched nothing: both "titles" here were `<p>`s. `SearchZeroMatchActions` keeps
                    its `<p>` because it genuinely mounts in two heading contexts; this branch has
                    exactly one — bare under the page `h1` on the blending branch — so a fixed level
                    is correct here and cannot be wrong anywhere else. Rank still comes from weight,
                    so nothing about the painted result changes (`EMPTY-STATES-104`). */}
                <h2 className="text-base font-semibold leading-6 text-foreground">
                    {localizer("search.blend_divider.no_matches")}
                </h2>
                {/* `DISCOVERY-51` — which sentence is TRUE depends on the other four tabs, and only
                    the page knows. `_everywhere` denies the whole site; `description` points at the
                    tabs. Choosing between them here is the same choice `SearchEmptyState` makes on
                    the two non-blending verticals, so one query no longer gets two different
                    verdicts depending on which tab it landed on. */}
                <p className="text-sm leading-6 text-muted-foreground">
                    {localizer(allVerticalsEmpty
                        ? "search.no_results.description_everywhere"
                        : "search.no_results.description")}
                </p>
            </div>
        );
    }

    return (
        <div
            className="flex items-center gap-3 py-1 text-xs text-muted-foreground"
            data-playwright-id="search-blend-divider"
            role="separator"
            aria-label={localizer("search.blend_divider.more")}
        >
            <span className="h-px flex-1 bg-border/60" />
            <span className="shrink-0 uppercase tracking-wide">
                {localizer("search.blend_divider.more")}
            </span>
            <span className="h-px flex-1 bg-border/60" />
        </div>
    );
}

export default SearchBlendDivider;
